import { ActionSet } from '@carbon/ibm-products/es/components/ActionSet/ActionSet';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  InlineLoading,
  InlineNotification,
  Link,
} from '@carbon/react';
import { Map as MapIcon } from '@carbon/react/icons';
import {
  NotificationGroup,
  AtomisedNotifications,
} from 'components/atomised-notifications';
import { NavigationGuard } from 'components/navigation-guard/navigation-guard';
import { useAtom, useAtomValue } from 'jotai';
import { HOST } from 'lib/request/request';
import { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import routes from '~/routes';
import { crmCreatePermissionLoadableAtom } from '../atoms';
import { showMapAtom } from './atoms';
import styles from './bulk-upload.module.scss';

export const TEMPLATE_HREF = `${HOST ?? ''}/crm/bulk_asset_batches/template`;

/** One ActionSet button. */
export interface FooterAction {
  key: string;
  kind: 'ghost' | 'secondary' | 'primary';
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
}

/** The footer while assets are being created, before or after submit. */
export const CREATING_ACTIONS: FooterAction[] = [
  { key: 'back', kind: 'secondary', label: 'Back', disabled: true },
  { key: 'create', kind: 'primary', label: 'Creating assets', loading: true },
];

/**
 * Whether the user may create CRM assets (GET /crm/permissions).
 * @return {object} Still loading, allowed, and the user's email
 */
export const useCreatePermission = () => {
  const permission = useAtomValue(crmCreatePermissionLoadableAtom);
  return {
    loading: permission.state === 'loading',
    allowed:
      permission.state === 'hasData' && !!permission.data?.hasPermissions,
    email: permission.state === 'hasData' ? permission.data?.email : null,
  };
};

interface BulkUploadShellProps {
  /** Adds "(Creation Results)" to the title. */
  results?: boolean;
  /** Ask before leaving (unsubmitted rows). */
  hasChanges?: boolean;
  /** Where the CRM breadcrumb leads. */
  crumbPath?: string;
  /** Show the body only to users who may create CRM assets. Viewing results does not need it. */
  requirePermission?: boolean;
  actions: FooterAction[];
  children: ReactNode;
}

/**
 * The page around every step: breadcrumbs, title, Show Map, helper copy, the
 * CRM permission gate, the page's notifications, and the footer action bar.
 * @param {BulkUploadShellProps} props - Title variant, guard, footer, body
 * @return {JSX.Element} The page
 */
export const BulkUploadShell = ({
  results = false,
  hasChanges = false,
  crumbPath = routes.crmTractsAndSubtractsPath,
  requirePermission = true,
  actions,
  children,
}: BulkUploadShellProps) => {
  const permission = useCreatePermission();
  const [showMap, setShowMap] = useAtom(showMapAtom);

  const renderBody = () => {
    if (!requirePermission) return children;
    if (permission.loading) {
      return <InlineLoading description="Checking CRM permission..." />;
    }
    if (!permission.allowed) {
      return (
        <InlineNotification
          kind="info"
          hideCloseButton
          title={`You ${
            permission.email ? `(${permission.email}) ` : ''
          }do not have permission to create CRM assets.`}
          subtitle="Please reach out to #salesforce-users Slack channel."
        />
      );
    }
    return children;
  };

  return (
    <div className={styles.page}>
      <NavigationGuard
        hasChanges={hasChanges}
        message="You have unsaved changes. Are you sure you want to leave?"
      />
      <header className={styles.header}>
        <Breadcrumb noTrailingSlash className={styles.breadcrumbs}>
          <BreadcrumbItem>
            <NavLink to={crumbPath}>CRM</NavLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>Bulk Upload Assets</BreadcrumbItem>
        </Breadcrumb>
        <div className={styles.titleRow}>
          <h1 className={styles.title}>
            Bulk Upload Assets
            {results && (
              <span className={styles.titleSuffix}> (Creation Results)</span>
            )}
          </h1>
          <Button
            kind="tertiary"
            size="md"
            renderIcon={MapIcon}
            onClick={() => setShowMap((shown) => !shown)}
          >
            {showMap ? 'Hide Map' : 'Show Map'}
          </Button>
        </div>
        <p className={styles.helper}>
          Upload a CSV or Excel file to add assets in bulk, or add them one at a
          time. Invalid rows have missing or invalid values and are skipped
          until you fix them. Need the right format?{' '}
          <Link href={TEMPLATE_HREF} download="bulk_asset_template.csv">
            Download the CSV template
          </Link>
          .
        </p>
      </header>
      <div className={styles.body}>
        <AtomisedNotifications groupIds={[NotificationGroup.BulkAssetBatch]} />
        {renderBody()}
      </div>
      <footer className={styles.footer} aria-label="Page actions">
        <ActionSet size="lg" actions={actions} />
      </footer>
    </div>
  );
};
